import { $getRoot } from "lexical";
import { channelToFormat } from "../../../../../utils/channel-to-format";
import type { UUID } from "../../../../../utils/uuid";
import type { ChannelName } from "../../../../channels/types";
import type { EmailDesign } from "../../../../email-design/server/supabase-email-design-service";
import type { PollWithOptions } from "../../../../poll/server/supabase-poll-service";
import type { SerialisedLexicalEditorDesign } from "../../types/serialised-editor-design";
import { calculatePaperSize } from "../convert-to-mjml/calculate-paper-size";
import { $convertToMjmlString } from "../convert-to-mjml/convert-to-mjml";
import { createConfiguredEditor } from "../headless-editor";
import { SlackMrkDwnTransformers } from "../markdown-transformers/slack-transformers";
import { TeamsMrkDwnTransformers } from "../markdown-transformers/teams-transformers";
import { getCharacterCount } from "../use-get-character-count";
import { chatBlockJson } from "./chat-block-json";
import { transformTwilio } from "./transform-twilio";

export type MessageBody = { body: string | null; text: string | null; characterCount?: number };

// The channel bodies a design turns into, independent of where the design and the account's data
// came from: useMessageBodyCreator loads those over supabase hooks, the api worker over postgrest.
export const createMessageBody = ({
  design,
  channel,
  account_id,
  emailDesign,
  polls = {},
}: {
  design: SerialisedLexicalEditorDesign;
  channel: NonNullable<ChannelName>;
  account_id: UUID;
  emailDesign?: EmailDesign;
  polls?: { [key: string]: PollWithOptions };
}): MessageBody | undefined => {
  const editor = createConfiguredEditor({
    onError: (error) => {
      console.error("Failed to generate content", error.message);
    },
    format: channelToFormat(channel),
  });
  editor.setEditorState(editor.parseEditorState(design));

  switch (channel) {
    case "work_email":
    case "personal_email":
    case "whatsapp":
      return editor.read(() => ({
        text: $getRoot().getTextContent(),
        body: $convertToMjmlString(
          editor,
          account_id,
          "paper",
          calculatePaperSize(emailDesign?.layout_settings),
          polls,
          emailDesign,
        ),
      }));
    case "slack":
      return chatBlockJson(
        editor,
        SlackMrkDwnTransformers,
        "slack",
        { maxWidth: 600 },
        account_id,
        polls,
      );
    case "teams":
      return chatBlockJson(
        editor,
        TeamsMrkDwnTransformers,
        "teams",
        { maxWidth: 580 },
        account_id,
        polls,
      );
    case "twilio":
      return {
        ...transformTwilio(editor),
        characterCount: getCharacterCount(editor.getEditorState()),
      };
  }
};
